/**
 * ObsidianTag — TipTap inline mark for `#tag` tokens.
 *
 * Stored as a mark (not a node) so adjacent text still flows naturally;
 * tag detection happens in the markdown-to-doc converter rather than at
 * input time, because input-rule-style tagging tends to fight users who
 * are typing C-style fenced code or YAML.
 */

import { Mark, mergeAttributes } from '@tiptap/core';

export interface ObsidianTagAttrs {
  tag: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    obsidianTag: {
      setObsidianTag: (attrs: ObsidianTagAttrs) => ReturnType;
      unsetObsidianTag: () => ReturnType;
    };
  }
}

export const ObsidianTag = Mark.create({
  name: 'obsidianTag',

  addAttributes() {
    return {
      tag: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-tag') ?? '',
        renderHTML: (attrs) => ({ 'data-tag': attrs.tag ?? '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span.vault-tag' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({ class: 'vault-tag' }, HTMLAttributes),
      0,
    ];
  },

  addCommands() {
    return {
      setObsidianTag:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
      unsetObsidianTag:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

const TAG_PATTERN = /(?:^|\s)#([A-Za-z0-9_/-]+)/g;

export interface TagToken {
  kind: 'text' | 'tag';
  value: string;
  attrs?: ObsidianTagAttrs;
}

export function tokenizeTags(input: string): TagToken[] {
  const out: TagToken[] = [];
  let cursor = 0;
  TAG_PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TAG_PATTERN.exec(input)) !== null) {
    // group 0 starts at the BOL-or-whitespace; the actual `#` index is
    // m.index + m[0].indexOf('#').
    const hashIdx = m.index + m[0].indexOf('#');
    if (cursor < hashIdx) {
      out.push({ kind: 'text', value: input.slice(cursor, hashIdx) });
    }
    const tag = m[1]!;
    out.push({ kind: 'tag', value: `#${tag}`, attrs: { tag } });
    cursor = hashIdx + tag.length + 1;
  }
  if (cursor < input.length) {
    out.push({ kind: 'text', value: input.slice(cursor) });
  }
  if (out.length === 0) out.push({ kind: 'text', value: input });
  return out;
}
